import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, useBreakpointValue, Skeleton} from '@chakra-ui/react'
import {ResponsiveBynderVideo} from '../../../components/responsive-bynder-video'
import {Helmet} from 'react-helmet'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {isHydrated} from '../../../utils/utils.js'
import ProductDyptichTile from '../../../components/product-dyptich-tile/index.jsx'

/**
 * PDC Home Product Dyptich Video
 * @param {string} product - Product ID
 * @param {string} subTitle - Subtitle
 * @param {string} desc - Description
 * @param {string} linkLabel - Link label
 * @param {string} smallVideo - Video media ID for small breakpoint
 * @param {string} smallVideoStructuredData - Video structured data for small breakpoint
 * @param {boolean} smallVideoAutoplay - Autoplay for small breakpoint
 * @param {string} mediumVideo - Video media ID for medium breakpoint
 * @param {string} mediumVideoStructuredData - Video structured data for medium breakpoint
 * @param {boolean} mediumVideoAutoplay - Autoplay for medium breakpoint
 * @param {string} largeVideo - Video media ID for large breakpoint
 * @param {string} largeVideoStructuredData - Video structured data for large breakpoint
 * @param {boolean} largeVideoAutoplay - Autoplay for large breakpoint
 * @param {string} xlargeVideo - Video media ID for xlarge breakpoint
 * @param {string} xlargeVideoStructuredData - Video structured data for xlarge breakpoint
 * @param {boolean} xlargeVideoAutoplay - Autoplay for xlarge breakpoint
 * @param {string} xxlargeVideo - Video media ID for xxlarge breakpoint
 * @param {string} xxlargeVideoStructuredData - Video structured data for xxlarge breakpoint
 * @param {boolean} xxlargeVideoAutoplay - Autoplay for xxlarge breakpoint
 * @param {string} textColor - Text color
 * @param {string} backgroundColor - Background color
 * @param {string} videoPlacement - Video placement
 * @returns {JSX.Element} - PDC Home Product Dyptich Video component
 */
export const PDCHomeProductDyptichVideo = ({
    product,
    subTitle,
    desc,
    linkLabel,
    smallVideo,
    smallVideoStructuredData,
    smallVideoAutoplay,
    mediumVideo,
    mediumVideoStructuredData,
    mediumVideoAutoplay,
    largeVideo,
    largeVideoStructuredData,
    largeVideoAutoplay,
    xlargeVideo,
    xlargeVideoStructuredData,
    xlargeVideoAutoplay,
    xxlargeVideo,
    xxlargeVideoStructuredData,
    xxlargeVideoAutoplay,
    textColor,
    backgroundColor,
    videoPlacement
}) => {
    // Choose video media ID based on breakpoint. If not set, we take the closest video media ID
    // from the previous breakpoints
    const videosPerBreakpoint = usePDCResponsiveElement({
        smallEl: smallVideo,
        mediumEl: mediumVideo,
        largeEl: largeVideo,
        xlargeEl: xlargeVideo,
        xxlargeEl: xxlargeVideo
    })

    // Choose video structured data based on breakpoint if the video media ID is set for the breakpoint
    const currentVideoStructuredData = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: smallVideoStructuredData,
            mediumEl: mediumVideoStructuredData,
            largeEl: largeVideoStructuredData,
            xlargeEl: xlargeVideoStructuredData,
            xxlargeEl: xxlargeVideoStructuredData,
            extraConditions: {
                conditionMd: mediumVideo,
                conditionLg: largeVideo,
                conditionXl: xlargeVideo,
                condition2xl: xxlargeVideo
            }
        })
    )
    // Choose autoplay based on breakpoint if the video media ID is set for the breakpoint
    const currentAutoplay = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: smallVideoAutoplay,
            mediumEl: mediumVideoAutoplay,
            largeEl: largeVideoAutoplay,
            xlargeEl: xlargeVideoAutoplay,
            xxlargeEl: xxlargeVideoAutoplay,
            extraConditions: {
                conditionMd: mediumVideo,
                conditionLg: largeVideo,
                conditionXl: xlargeVideo,
                condition2xl: xxlargeVideo
            }
        })
    )

    const titleId = useId()

    const videoBlock =
        isHydrated() && videosPerBreakpoint ? (
            <Box width={['100%', '100%', '50%']} position="relative">
                <ResponsiveBynderVideo mediaIds={videosPerBreakpoint} autoplay={currentAutoplay} />
            </Box>
        ) : (
            <Skeleton aspectRatio={'1'} width={['100%', '100%', '50%']} />
        )

    return (
        <Box
            css={[PDCHomeProductDyptichVideo.css]}
            as="section"
            aria-describedby={titleId}
            display="flex"
            flexDirection={['column', 'column', 'row']}
            justifyContent="space-between"
            alignItems="stretch"
            width="100%"
            height={'auto'}
            backgroundColor={backgroundColor}
            color={textColor}
        >
            {currentVideoStructuredData && (
                <Helmet>
                    <script type="application/ld+json">{currentVideoStructuredData}</script>
                </Helmet>
            )}

            {videoPlacement === 'left (top)' ? (
                <>
                    {videoBlock}
                    <ProductDyptichTile
                        product={product}
                        subTitle={subTitle}
                        desc={desc}
                        linkLabel={linkLabel}
                        titleId={titleId}
                        textColor={textColor}
                    />
                </>
            ) : (
                <>
                    <ProductDyptichTile
                        product={product}
                        subTitle={subTitle}
                        desc={desc}
                        linkLabel={linkLabel}
                        titleId={titleId}
                        textColor={textColor}
                    />
                    {videoBlock}
                </>
            )}
        </Box>
    )
}

PDCHomeProductDyptichVideo.propTypes = {
    product: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    desc: PropTypes.string,
    linkLabel: PropTypes.string.isRequired,
    smallVideo: PropTypes.string.isRequired,
    smallVideoStructuredData: PropTypes.string,
    smallVideoAutoplay: PropTypes.bool,
    mediumVideo: PropTypes.string,
    mediumVideoStructuredData: PropTypes.string,
    mediumVideoAutoplay: PropTypes.bool,
    largeVideo: PropTypes.string,
    largeVideoStructuredData: PropTypes.string,
    largeVideoAutoplay: PropTypes.bool,
    xlargeVideo: PropTypes.string,
    xlargeVideoStructuredData: PropTypes.string,
    xlargeVideoAutoplay: PropTypes.bool,
    xxlargeVideo: PropTypes.string,
    xxlargeVideoStructuredData: PropTypes.string,
    xxlargeVideoAutoplay: PropTypes.bool,
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    videoPlacement: PropTypes.string
}
